import {
  CONSIDERATION_TYPES,
  INSURANCE_PROVIDERS,
} from '~/app/modules/insurance/utils/insurance.const';
import { WEB_ROUTES } from '~/utils/webConfig';
import { APPLICATION_OPERATOR_OFFER_DECREASE, VERIFICATION_OPTIONS } from '~/utils/consts';
import { SocialFundRoutes } from '~/app/features/social-fund/utils/consts';

export default async ({ app, route, store, query, softRedirect }) => {
  const {
    CLIENT_BLOCKED,
    CLIENT_BLACKLISTED,
    APPLICATION_ACCEPTED,
    APPLICATION_COMPLETED,
    APPLICATION_CANCELED,
    APPLICATION_REJECTED,
    APPLICATION_TERMINATED,
    APPLICATION_SPECIAL_OFFER,
    APPLICATION_YHAT_DECREASE,
    APPLICATION_YHAT_INCREASE,
    APPLICATION_MANUAL_VERIFICATION,
    APPLICATION_VERIFICATION,
    APPLICATION_SIGNING,
    APPLICATION_DELAY_DECISION,
  } = app.$tools.status;

  // Redirect to first step if not logged in
  if (!app.$auth.loggedIn) {
    if (process.client) {
      localStorage.removeItem('promoCode');
    }
    return softRedirect(app.localePath(WEB_ROUTES.REGISTRATION_STEP_1));
  }

  await app.$auth.fetchUser();

  const { profile } = app.$client;
  const { info } = profile;
  const [routeName] = (route.name || '').split('___');

  // Logout blacklisted client
  if (
    CLIENT_BLOCKED === profile.status ||
    (CLIENT_BLACKLISTED === profile.status && !profile.canLogin)
  ) {
    return app.$auth.logout();
  }

  // Redirect to 2nd step if documentNumber are not set
  if (!profile.documentNumber || !profile.contactPerson.length) {
    return softRedirect(app.localePath(WEB_ROUTES.REGISTRATION_STEP_2));
  }

  // Redirect to 3rd step if employment are not set
  const noEmploymentInfo = Object.keys(info).length && !Object.keys(info.employment).length;
  const noFinanceInfo = !info.finance.additionalIncome.amount;

  if (noEmploymentInfo || noFinanceInfo) {
    return softRedirect(app.localePath(WEB_ROUTES.REGISTRATION_STEP_3));
  }

  // Redirect to 4th step
  if (profile.isIdDocumentAuthenticationRequired) {
    return softRedirect(app.localePath(WEB_ROUTES.REGISTRATION_STEP_4));
  }

  if (!profile.socialFundConsent.canBeSkipped) {
    return softRedirect(app.localePath(SocialFundRoutes.social_fund));
  }

  // Redirect to 6th step if wallet or bank are not set
  if (!Object.keys(info.wallet).length && !Object.keys(info.bank.branch).length) {
    return softRedirect(app.localePath(WEB_ROUTES.REGISTRATION_STEP_6));
  }

  // Get active application
  await store.dispatch('client/getApplication');

  const { application } = app.$client;
  const applicationStatus = application && application.status;
  const applicationConsiderationType = application && application.considerationType;

  const considerationTypeRoutes = [
    WEB_ROUTES.CONSIDERATION_TYPE_INSURANCE,
    WEB_ROUTES.CONSIDERATION_TYPE_GUARANTOR,
    WEB_ROUTES.CONSIDERATION_TYPE_WITHOUT_INSURANCE,
  ];
  const insuranceProviders = [INSURANCE_PROVIDERS.SILK_ROAD, INSURANCE_PROVIDERS.A_PLUS];

  if (applicationStatus === APPLICATION_DELAY_DECISION) {
    if (profile.hasActiveLoan && insuranceProviders.includes(profile.insuranceProvider)) {
      return softRedirect(app.localePath(WEB_ROUTES.CONSIDERATION_TYPE_INSURANCE));
    }

    if (applicationConsiderationType === CONSIDERATION_TYPES.WITH_GUARANTOR) {
      if (route.name.includes(WEB_ROUTES.CONSIDERATION_TYPE_WITHOUT_INSURANCE)) {
        if (
          application.availableConsiderationTypes.includes(CONSIDERATION_TYPES.WITHOUT_INSURANCE)
        ) {
          return true;
        }
      }

      if (route.name.includes(WEB_ROUTES.CONSIDERATION_TYPE_INSURANCE)) {
        return true;
      }

      return softRedirect(app.localePath(WEB_ROUTES.CONSIDERATION_TYPE_DELAY_DECISION));
    }
  }

  if (
    [CONSIDERATION_TYPES.DRAFT, CONSIDERATION_TYPES.WITHOUT_INSURANCE].includes(
      applicationConsiderationType
    )
  ) {
    if (
      [
        APPLICATION_DELAY_DECISION,
        APPLICATION_YHAT_DECREASE,
        APPLICATION_SPECIAL_OFFER,
        APPLICATION_YHAT_INCREASE,
      ].includes(applicationStatus)
    ) {
      const inAllowedRoutes = considerationTypeRoutes.some(allowedRoute =>
        route.name.includes(allowedRoute)
      );

      if (inAllowedRoutes) {
        return true;
      }

      if ([APPLICATION_SPECIAL_OFFER, APPLICATION_YHAT_DECREASE].includes(applicationStatus)) {
        return softRedirect(app.localePath(WEB_ROUTES.APPLICATION_OFFER_DECREASE));
      }

      if (applicationStatus === APPLICATION_YHAT_INCREASE) {
        return softRedirect(app.localePath(WEB_ROUTES.APPLICATION_OFFER_INCREASE));
      }

      return softRedirect(app.localePath(WEB_ROUTES.CONSIDERATION_TYPE));
    }
  }

  if ([APPLICATION_SPECIAL_OFFER, APPLICATION_YHAT_DECREASE].includes(applicationStatus)) {
    return softRedirect(app.localePath(WEB_ROUTES.APPLICATION_OFFER_DECREASE));
  }

  if (applicationStatus === APPLICATION_OPERATOR_OFFER_DECREASE) {
    return softRedirect(app.localePath(WEB_ROUTES.APPLICATION_OPERATOR_OFFER_DECREASE));
  }

  if (applicationStatus === APPLICATION_YHAT_INCREASE) {
    return softRedirect(app.localePath(WEB_ROUTES.APPLICATION_OFFER_INCREASE));
  }

  if (profile && profile.showRolloverOtpForm) {
    return softRedirect(app.localePath(WEB_ROUTES.ROLLOVER_OTP));
  }

  if (applicationStatus === APPLICATION_SIGNING) {
    if (application.verificationType === VERIFICATION_OPTIONS.VIDEO) {
      return softRedirect(app.localePath(WEB_ROUTES.VIDEO_CAPTURE));
    }

    if (application.verificationType === VERIFICATION_OPTIONS.BRANCH) {
      return softRedirect(app.localePath(WEB_ROUTES.MANUAL_VERIFICATION));
    }

    return softRedirect(app.localePath(WEB_ROUTES.SELECT_VERIFICATION));
  }

  // Stay at error page if called intentionally
  if (routeName === 'application-rejected' && query.f) {
    return softRedirect(app.localePath('application-rejected'));
  }

  // Redirect to rejected page if application was rejected
  if (applicationStatus === APPLICATION_REJECTED && application.canNotMakeApplication) {
    return softRedirect(app.localePath('application-rejected'));
  }

  // Redirect to success page if application was accepted
  if (applicationStatus === APPLICATION_ACCEPTED) {
    return softRedirect(app.localePath('application-accepted'));
  }

  if (applicationStatus === APPLICATION_MANUAL_VERIFICATION) {
    if (application.verificationType === VERIFICATION_OPTIONS.VIDEO) {
      return profile.videoUploadRequired
        ? softRedirect(app.localePath(WEB_ROUTES.VIDEO_CAPTURE))
        : softRedirect(app.localePath(WEB_ROUTES.APPLICATION_LOADING_VIDEO));
    }

    return softRedirect(app.localePath(WEB_ROUTES.MANUAL_VERIFICATION));
  }

  if (applicationStatus === APPLICATION_VERIFICATION) {
    if (application.verificationType === VERIFICATION_OPTIONS.VIDEO) {
      return softRedirect(app.localePath(WEB_ROUTES.VIDEO_CAPTURE));
    }

    if (application.verificationType === VERIFICATION_OPTIONS.BRANCH) {
      return softRedirect(app.localePath(WEB_ROUTES.MANUAL_VERIFICATION));
    }

    return softRedirect(app.localePath(WEB_ROUTES.APPLICATION_LOADING));
  }

  // Redirect to loading page if application is processing
  if (
    applicationStatus &&
    ![
      APPLICATION_COMPLETED,
      APPLICATION_REJECTED,
      APPLICATION_CANCELED,
      APPLICATION_TERMINATED,
    ].includes(applicationStatus)
  ) {
    return softRedirect(app.localePath('application-loading'));
  }

  if (routeName === 'application-apply') {
    return true;
  }

  if (!profile.hasActiveLoan) {
    return softRedirect(app.localePath('profile-no-loan'));
  }

  await store.dispatch('client/getLoan');
  await store.dispatch('client/getActivePaymentSchedule');
  return softRedirect(app.localePath('profile-my-loan'));
};
